import {
  Box,
  Typography,
  useMediaQuery,
  useTheme,
  TextField,
  Link,
  CircularProgress,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { BizboxLogo } from "../../../common/components/bizbox-logo";
import {
  useResendTwoFactorCodeCode,
  useVerifyTwoFactorCode,
} from "../api/hooks";
import { useSignInStore } from "../api/stores";
import { LoadingButton } from "@mui/lab";
import { VerifyTwoFactorModel } from "../api/types";
import { decrypt } from "../../../common/utils/decryption-service";
import { useSnackbar } from "notistack";

export const ChangeEmailForm: React.FC = () => {
  const { handleSubmit } = useForm<VerifyTwoFactorModel>();
  const { enqueueSnackbar } = useSnackbar();

  const theme = useTheme();
  const showLogo = useMediaQuery(theme.breakpoints.down("laptop"));
  const {
    value1,
    value2,
    value3,
    value4,
    value5,
    value6,
    setValue1,
    setValue2,
    setValue3,
    setValue4,
    setValue5,
    setValue6,
  } = useSignInStore();
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const location = useLocation();
  const { email, remember } = location.state || {};
  const didInitialLockoutCheck = useRef(false);

  const decryptedEmail = useMemo(() => {
    if (!email || typeof email !== "string") return "";
    try {
      const plain = decrypt(email);
      return plain || "";
    } catch {
      return "";
    }
  }, [email]);

  const { mutate: verifyCode } = useVerifyTwoFactorCode({
    onError: (error) => {
      const message =
        typeof error === "string"
          ? error
          : "Invalid code. Please click \"Resend Code\" to try again.";
      setOtpError(message);
      enqueueSnackbar(message, { variant: "error" });
      console.error("OTP Verification Failed:", error);
    },
  });

  const maskEmail = (email: string) => {
    const [localPart, domain] = email.split("@");
    const maskedLocalPart = localPart.slice(0, 2) + "**" + localPart.slice(-2);
    return `${maskedLocalPart}@${domain}`;
  };

  const onSubmit = () => {
    const combinedValue = value1 + value2 + value3 + value4 + value5 + value6;
    setOtpError("");
    verifyCode({
      email: email,
      code: combinedValue,
      otp: combinedValue,
      remember: remember,
    });
  };

  useEffect(() => {
    return () => {
      setValue1("");
      setValue2("");
      setValue3("");
      setValue4("");
      setValue5("");
      setValue6("");
    };
  }, [setValue1, setValue2, setValue3, setValue4, setValue5, setValue6]);

  const [resendCount, setResendCount] = useState(0);
  const { mutate: resendOtp } = useResendTwoFactorCodeCode({
    onSuccess: (data: string) => {
      if (data.includes("minute(s)")) {
        setErrorMessage(data);
        const parts = data.match(/\d+/g);
        if (parts && parts.length >= 2) {
          const mins = parseInt(parts[0], 10);
          const secs = parseInt(parts[1], 10);
          const total = mins * 60 + secs;
          startCountdown(total);
        }
        setResendCount((prevResendCount) => prevResendCount + 1);
      } else {
        setErrorMessage("");
      }
    },
    onError: (error) => {
      if (error.includes("minute(s)")) {
        setErrorMessage(error);
        const parts = error.match(/\d+/g);
        if (parts && parts.length >= 2) {
          const mins = parseInt(parts[0], 10);
          const secs = parseInt(parts[1], 10);
          const total = mins * 60 + secs;
          startCountdown(total);
        }
      } else {
        setErrorMessage("");
      }
    },
  });

  const [errorMessage, setErrorMessage] = useState("");
  const [otpError, setOtpError] = useState("");
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [countdown]);

  const startCountdown = (seconds: number) => {
    setCountdown(seconds);
  };

  const isResendDisabled = countdown > 0;

  const handleResendCode = () => {
    resendOtp({ email: email, checkLockoutOnly: false });
  };

  useEffect(() => {
    if (resendCount === 0 && email && !didInitialLockoutCheck.current) {
      resendOtp({ email, checkLockoutOnly: true });
      didInitialLockoutCheck.current = true;
    }
    return () => {
      setValue1("");
      setValue2("");
      setValue3("");
      setValue4("");
      setValue5("");
      setValue6("");
    };
  }, [email]);

  const setters = [
    setValue1,
    setValue2,
    setValue3,
    setValue4,
    setValue5,
    setValue6,
  ];

  const handleKeyDown = (i: number) => (e: React.KeyboardEvent) => {
    if (
      e.key === "Backspace" &&
      ![value1, value2, value3, value4, value5, value6][i] &&
      inputsRef.current[i - 1]
    ) {
      inputsRef.current[i - 1]!.focus();
    }
  };

  const handleChange =
    (i: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const digit = e.target.value.replace(/\D/, "").slice(-1);
      setters[i](digit);

      if (digit && inputsRef.current[i + 1]) {
        inputsRef.current[i + 1]!.focus();
      }
    };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
        width: "100%",
        padding: 3,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          height: "inherit",
          maxWidth: "466px",
          width: "100%",
          margin: "auto",
        }}
      >
        {showLogo && (
          <Box
            sx={{
              mt: "20px",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                width: "47px",
                height: "47px",
                m: 0,
                p: 0,
                display: "block",
              }}
            >
              <BizboxLogo />
            </Box>
          </Box>
        )}
        <Typography
          fontWeight={500}
          variant="h1"
          sx={{ mb: 10 }}
          align="center"
          color="primary.main"
        >
          One Time Pin
        </Typography>
        <Typography
          fontSize={16}
          fontWeight={500}
          align="left"
          sx={{ mb: 3 }}
          color="text.secondary"
        >
          We sent a 6-digit code to your email{" "}
          {decryptedEmail ? maskEmail(decryptedEmail) : ""}
        </Typography>
        <Typography
          fontSize={12}
          fontWeight={300}
          align="left"
          sx={{ mb: 3 }}
          color="text.secondary"
        >
          Please enter the authentication code
        </Typography>

        {/* OTP Input Fields */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            mt: "5px",
            gap: 1,
            width: "100%",
          }}
        >
          {[value1, value2, value3, value4, value5, value6].map(
            (value, index) => (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  width: "60vw",
                  mt: "5px",
                }}
              >
                <TextField
                  key={index}
                  inputRef={(el) => (inputsRef.current[index] = el)}
                  onKeyDown={handleKeyDown(index)}
                  sx={{
                    "& .MuiInputBase-root": {
                      height: 134,
                      maxWidth: "78px",
                      fontSize: "2.5rem",
                      minWidth: "65px",
                    },
                    "& .MuiInputBase-input": {
                      textAlign: "center",
                      color: "#123257",
                    },
                  }}
                  value={value}
                  onChange={handleChange(index)}
                  variant="outlined"
                  inputProps={{
                    maxLength: 1,
                    inputMode: "numeric",
                  }}
                />
              </Box>
            )
          )}
        </Box>

        {errorMessage && (
          <p
            style={{
              color: resendCount === 1 ? "black" : "red",
              fontSize: "12px",
            }}
          >
            {countdown > 0
              ? resendCount === 1
                ? `${Math.floor(countdown / 60)}m ${countdown % 60}s`
                : `Please try resending the code again in ${Math.floor(
                    countdown / 60
                  )}m ${countdown % 60}s.`
              : ""}
          </p>
        )}

        {otpError && (
          <Typography sx={{ mt: 1 }} fontSize={"12px"} color="error">
            {otpError}
          </Typography>
        )}

        <Typography
          fontSize={"12px"}
          fontWeight={300}
          align="center"
          sx={{ mt: 3 }}
          color="#637B9B"
        >
          Didn't receive the code?
          <Link
            fontWeight={500}
            fontSize={"14px"}
            sx={{
              marginLeft: 0.5,
              textDecoration: "none",
              cursor: isResendDisabled ? "default" : "pointer",
              pointerEvents: isResendDisabled ? "none" : "auto",
              color: isResendDisabled ? "#AAA" : "#FF7F6C",
            }}
            onClick={isResendDisabled ? undefined : handleResendCode}
            aria-disabled={isResendDisabled}
          >
            {""}Resend Code
          </Link>
        </Typography>

        <LoadingButton
          type="submit"
          loadingIndicator={
            <CircularProgress size={18} sx={{ color: "white" }} />
          }
          sx={{
            backgroundColor: "primary.main",
            color: "white",
            mt: 5,
            borderRadius: "12px",
            fontWeight: "bold",
            fontSize: "1.2rem",
          }}
        >
          Submit
        </LoadingButton>

        <Typography
          fontSize={"15px"}
          fontWeight={500}
          align="center"
          sx={{ mt: 5 }}
          color="#6D87AA"
        >
          1.0.28
        </Typography>
      </Box>
    </Box>
  );
};
